import React, { useMemo } from "react";
import { AlertTriangle, Plus } from "lucide-react";

/**
 * Detects common forgotten items in a quote based on simple heuristics.
 * Returns 0-3 actionable suggestions the user can add in one click.
 */
const RULES = [
  {
    id: "primer",
    triggers: ["peinture", "paint"],
    blockedBy: ["primer", "apprêt", "appret"],
    fr: { label: "Primer / apprêt manquant", suggest: "Ajouter un apprêt scellant 1 couche",
      item: { description: "Apprêt scellant 1 couche (Benjamin Moore)", unit: "pi²", quantity: 0, material_cost: 0.20, labor_cost: 0.35, equipment_cost: 0, subcontractor_cost: 0, profit_pct: 15 } },
    en: { label: "Missing primer", suggest: "Add 1-coat sealing primer",
      item: { description: "1-coat sealing primer (Benjamin Moore)", unit: "sq.ft", quantity: 0, material_cost: 0.20, labor_cost: 0.35, equipment_cost: 0, subcontractor_cost: 0, profit_pct: 15 } },
  },
  {
    id: "joint",
    triggers: ["gypse", "drywall", "placoplâtre", "placo"],
    blockedBy: ["tirage", "joint", "compound"],
    fr: { label: "Tirage de joints absent", suggest: "Ajouter tirage de joints niveau 4",
      item: { description: "Tirage joints niveau 4 (3 couches)", unit: "pi²", quantity: 0, material_cost: 0.25, labor_cost: 1.20, equipment_cost: 0.05, subcontractor_cost: 0, profit_pct: 18 } },
    en: { label: "Joint compound missing", suggest: "Add level-4 joint compound",
      item: { description: "Joint compound level 4 (3 coats)", unit: "sq.ft", quantity: 0, material_cost: 0.25, labor_cost: 1.20, equipment_cost: 0.05, subcontractor_cost: 0, profit_pct: 18 } },
  },
  {
    id: "cleanup",
    triggers: ["construction", "rénovation", "renovation", "demolition", "démolition"],
    blockedBy: ["nettoyage", "cleanup", "ménage"],
    fr: { label: "Nettoyage de fin de chantier", suggest: "Ajouter nettoyage post-construction",
      item: { description: "Nettoyage post-construction (planchers, vitres, dépoussiérage)", unit: "pi²", quantity: 0, material_cost: 0.05, labor_cost: 0.55, equipment_cost: 0.10, subcontractor_cost: 0, profit_pct: 15 } },
    en: { label: "Final site cleanup missing", suggest: "Add post-construction cleanup",
      item: { description: "Post-construction cleanup (floors, windows, dusting)", unit: "sq.ft", quantity: 0, material_cost: 0.05, labor_cost: 0.55, equipment_cost: 0.10, subcontractor_cost: 0, profit_pct: 15 } },
  },
  {
    id: "subfloor",
    triggers: ["plancher", "floor", "vinyle", "lvt", "céramique", "ceramique", "tile"],
    blockedBy: ["sous-plancher", "subfloor", "nivellement", "leveling", "préparation", "preparation"],
    fr: { label: "Préparation sous-plancher omise", suggest: "Ajouter nivellement Mapei",
      item: { description: "Préparation sous-plancher (nivellement Mapei Planiprep)", unit: "pi²", quantity: 0, material_cost: 0.60, labor_cost: 0.95, equipment_cost: 0.05, subcontractor_cost: 0, profit_pct: 15 } },
    en: { label: "Subfloor prep missing", suggest: "Add Mapei leveling",
      item: { description: "Subfloor preparation (Mapei Planiprep leveling)", unit: "sq.ft", quantity: 0, material_cost: 0.60, labor_cost: 0.95, equipment_cost: 0.05, subcontractor_cost: 0, profit_pct: 15 } },
  },
];

export default function MissingItemsHint({ form, lang, onAdd }) {
  const suggestions = useMemo(() => {
    const haystack = (
      (form.work_description || "") + " " +
      (form.scope_of_work || "") + " " +
      (form.work_type || "") + " " +
      (form.items || []).map((i) => i.description || "").join(" ")
    ).toLowerCase();
    const out = [];
    for (const r of RULES) {
      const hasTrigger = r.triggers.some((t) => haystack.includes(t));
      if (!hasTrigger) continue;
      const hasBlocker = r.blockedBy.some((b) => haystack.includes(b));
      if (hasBlocker) continue;
      out.push(r);
      if (out.length >= 3) break;
    }
    return out;
  }, [form]);

  if (suggestions.length === 0) return null;

  return (
    <div className="glass-card p-4 mb-6" style={{ borderColor: "rgba(255,193,7,0.3)", background: "rgba(255,193,7,0.04)" }}
      data-testid="missing-items-hint">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={15} color="#FFC107" />
        <h4 className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#FFC107" }}>
          {lang === "fr" ? "Suggestions IA · Oublis fréquents" : "AI · Common omissions"}
        </h4>
      </div>
      <div className="space-y-2">
        {suggestions.map((s) => {
          const loc = lang === "fr" ? s.fr : s.en;
          return (
            <div key={s.id} className="flex items-center justify-between gap-3 p-2 rounded-lg" style={{ background: "rgba(0,0,0,0.2)" }}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold" style={{ color: "#F5F5F5" }}>{loc.label}</div>
                <div className="text-[11px]" style={{ color: "rgba(245,245,245,0.55)" }}>{loc.suggest}</div>
              </div>
              <button type="button" onClick={() => onAdd(loc.item)}
                className="btn-ghost text-xs flex-shrink-0" data-testid={`missing-add-${s.id}`}>
                <Plus size={12} /> {lang === "fr" ? "Ajouter" : "Add"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
